import { createSlice } from '@reduxjs/toolkit';
import type {
  BBox,
  CleanWFSFeature,
} from '../../models/geoserver/geoserver.model';
import type { VesselSuperType } from '../../models/vessels/all-ship-types';

type GlobalAisState = {
  loading: boolean;
  rawData: CleanWFSFeature[];
  filteredData: CleanWFSFeature[];
  showVesselsByType: VesselSuperType[];
  showFlags: string[];
  daasToken: string | null;
  daasTokenExpiry: number | null;
  error: boolean;
  lastBbox: BBox;
  globalAisFilterAccordions: string | null;
};

export const INITIAL_GLOBAL_AIS_STATE: GlobalAisState = {
  loading: false,
  rawData: [],
  filteredData: [],
  showVesselsByType: [],
  showFlags: [],
  daasToken: null,
  daasTokenExpiry: null,
  error: false,
  lastBbox: [] as unknown as BBox,
  globalAisFilterAccordions: null,
};

const GlobalAisSlice = createSlice({
  name: 'globalAis',
  initialState: INITIAL_GLOBAL_AIS_STATE,
  reducers: {
    setLoading: (state) => {
      state.loading = true;
    },
    setLoadingOff: (state) => {
      state.loading = false;
    },
    setRawData: (state, action) => {
      state.rawData = action.payload;
    },
    setFilteredData: (state, action) => {
      state.filteredData = action.payload;
    },
    setShowVesselsByType: (state, action) => {
      state.showVesselsByType = action.payload;
    },
    addShowVesselByType: (state, action) => {
      const showVesselsByType: VesselSuperType[] = action.payload;
      showVesselsByType.forEach((showVesselByType) => {
        if (!state.showVesselsByType.includes(showVesselByType)) {
          state.showVesselsByType.push(showVesselByType);
        } else {
          state.showVesselsByType = state.showVesselsByType.filter(
            (showVesselByType_) => showVesselByType_ !== showVesselByType
          );
        }
      });
    },
    clearShowVesselsByType: (state) => {
      state.showVesselsByType = [];
    },
    setShowFlags: (state, action) => {
      state.showFlags = action.payload;
    },
    addShowFlags: (state, action) => {
      const flags: string[] = action.payload;
      flags.forEach((flag) => {
        if (!state.showFlags.includes(flag)) {
          state.showFlags.push(flag);
        } else {
          state.showFlags = state.showFlags.filter((flag_) => flag_ !== flag);
        }
      });
    },
    clearShowFlags: (state) => {
      state.showFlags = [];
    },
    setDaasToken: (state, action) => {
      state.daasToken = action.payload;
    },
    setDaasTokenExpiry: (state, action) => {
      state.daasTokenExpiry = action.payload;
    },
    setError(state) {
      state.error = true;
    },
    clearError(state) {
      state.error = false;
    },
    setBBox(state, action) {
      state.lastBbox = action.payload;
    },
    setGlobalAisFilterAccordions(state, action) {
      state.globalAisFilterAccordions = action.payload;
    },
  },
});

export const {
  setLoading,
  setLoadingOff,
  setRawData,
  setFilteredData,
  setShowVesselsByType,
  addShowVesselByType,
  clearShowVesselsByType,
  setShowFlags,
  addShowFlags,
  clearShowFlags,
  setDaasToken,
  setDaasTokenExpiry,
  setError,
  clearError,
  setBBox,
  setGlobalAisFilterAccordions,
} = GlobalAisSlice.actions;

export default GlobalAisSlice.reducer;
